// pages/PortalPage.js —— 门户页：产品卡片入口 + 切换公司 + 用户菜单 + 异常提醒
// 依据《云平台账号权限》手册：多产品时进门户、单产品直接跳转；右上角个人中心 / 退出登录；通用域名支持「切换公司」。
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Badge } from 'antd';
import {
  ClusterOutlined, BlockOutlined, HeatMapOutlined, DeploymentUnitOutlined,
  SettingOutlined, LogoutOutlined, UserOutlined, DownOutlined, BankOutlined,
  BellOutlined, GlobalOutlined, CloseOutlined, SwapOutlined,
} from '@ant-design/icons';
import { api } from '../services/api';
import { PORTAL_COMPANY_LIST, PORTAL_NOTICES, PORTAL_ROLES, STATIONS } from '../config/stationData';
import { PRESET_PERMS } from './portal/SettingsPage';
import { isSuperAdmin } from '../utils/auth';
import i18n from '../i18n';

// 支持的语言（依据手册 p23：点击地球图标切换系统语言，含小语种）
const LANGS = [
  { key: 'zh', label: '简体中文' },
  { key: 'en', label: 'English' },
  { key: 'th', label: 'ไทย' },
];

// 缺陷157：光储充 / 削峰填谷两个产品分别取各自类型的站点，保证进入的控制台页面不同
const firstStationOf = (type) => (STATIONS.find((s) => s.type === type) || STATIONS[0]).id;
const PV_STATION_ID = firstStationOf('pvc');
const PEAK_STATION_ID = firstStationOf('peak');

const PRODUCTS = [
  {
    key: 'storage',
    name: '能源管家',
    desc: '多站点能源管理主体平台：展示、监控、报警、分析、运维与运营全流程。',
    icon: <ClusterOutlined />,
    badge: '已开通',
    path: '/app/display/multi',
  },
  {
    key: 'peak',
    name: '削峰填谷单站控制',
    desc: '削峰填谷单站控制：实时运行、主接线图、电池与 PCS 曲线、保护策略。',
    icon: <HeatMapOutlined />,
    badge: '已开通',
    // 缺陷157：进入削峰填谷（peak）控制台，避免与光储充产品打开同一页面
    path: `/app/monitor/station?station=${PEAK_STATION_ID}`,
  },
  {
    key: 'pv',
    name: '光储充单站控制',
    desc: '光储充一体化单站控制：设备监控、统计报表、策略参数与计划曲线。',
    icon: <BlockOutlined />,
    badge: '已开通',
    // 缺陷157：按产品类型带对应类型的站点，进入光储充（pvc）控制台
    path: `/app/monitor/station?station=${PV_STATION_ID}`,
  },
  {
    key: 'video',
    name: '视频监控',
    desc: '接入站点安防摄像头，远程查看现场画面与历史录像。',
    icon: <DeploymentUnitOutlined />,
    badge: '待开通',
    // 仍显示「待开通」，但允许进入页面预览（不走待开通拦截）
    allowEnter: true,
    path: '/app/monitor/video',
  },
  {
    key: '3d',
    name: '三维可视化',
    desc: '站点三维建模与实时数据叠加展示，直观呈现运行态势。',
    icon: <DeploymentUnitOutlined />,
    badge: '增值功能',
    vip: true,
    path: '/app/display/3d',
  },
];

// 产品 → 入口所在菜单（判断角色菜单可见性，依据《云平台账号权限》）
const PRODUCT_MENUS = {
  storage: ['/app/display/multi'],
  pv: ['/app/monitor/station'],
  peak: ['/app/monitor/station'],
};

const PortalPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ name: '用户' });
  const [company, setCompany] = useState(PORTAL_COMPANY_LIST[0].name);
  const [notice, setNotice] = useState('');
  const [lng, setLng] = useState(i18n.language || 'zh');
  const superAdmin = isSuperAdmin(user);

  // 平台名称（个性配置保存后生效，依据手册 p16：门户页展示平台名称）
  const platformName = lng === 'en'
    ? (localStorage.getItem('platformNameEn') || 'Energy Management Cloud')
    : (localStorage.getItem('platformNameZh') || '智慧能源管理云平台');

  useEffect(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) {
      try { setUser(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
    const c = localStorage.getItem('currentCompany');
    if (c) {
      // 兼容历史数据：currentCompany 可能被存成 JSON 对象
      try {
        const o = JSON.parse(c);
        setCompany(typeof o === 'string' ? o : (o.name || o.short || c));
      } catch (e) { setCompany(c); }
    }
  }, []);

  const handleLogout = async () => {
    try {
      const saved = localStorage.getItem('currentUser');
      if (saved) {
        const { id, name } = JSON.parse(saved);
        await api.logout({ userId: id, username: name });
      }
    } catch (e) { /* ignore */ }
    // 依据手册 p24：退出登录会退出浏览器中所有云平台标签页的登录状态，回到登录页
    ['currentUser', 'sessionToken', 'loginIp', 'currentCompany'].forEach((k) => localStorage.removeItem(k));
    localStorage.setItem('logoutAt', String(Date.now()));
    navigate('/');
  };

  const enterProduct = (p) => {
    // 三维可视化：非超级管理员视为增值功能未开通
    if (p.key === '3d' && !isSuperAdmin(user)) {
      setNotice('增值功能待开通');
      return;
    }
    // 依据手册 p24：异常提醒 —— 产品未开通 / 无产品使用权 → 提示联系管理员
    // allowEnter：徽章仍为「待开通」，但允许跳转（如视频监控）
    if (p.badge === '待开通' && !p.allowEnter) {
      setNotice(PORTAL_NOTICES[0].text);
      return;
    }
    // 依据《云平台账号权限》：角色权限仅控制菜单可见性
    const roleName = PORTAL_ROLES.find((r) => Number(r.key) === Number(user.role_id))?.name || '管理员';
    // 「储能车」角色进入储能管家后直接进入单站
    if (roleName === '储能车' && p.key === 'storage') {
      window.open(`/app/monitor/station?station=${firstStationOf('ems')}`, '_blank');
      return;
    }
    const needMenus = PRODUCT_MENUS[p.key];
    if (needMenus && !needMenus.some((m) => (PRESET_PERMS[roleName] || []).includes(m))) {
      setNotice(PORTAL_NOTICES[1].text);
      return;
    }
    // 依据手册 p23：点击产品卡片会在浏览器中打开新标签页进入对应产品
    window.open(p.path, '_blank');
  };

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: '个人中心', onClick: () => navigate('/portal/profile') },
      { key: 'settings', icon: <SettingOutlined />, label: '设置中心', onClick: () => navigate('/portal/settings') },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
    ],
  };

  const companyMenu = {
    items: [
      { key: 'label', label: '切换公司', disabled: true },
      { type: 'divider' },
      ...PORTAL_COMPANY_LIST.map((c) => ({
        key: c.key,
        label: `${c.name}（${c.domain}）`,
        onClick: () => {
          setCompany(c.name);
          localStorage.setItem('currentCompany', c.name);
          // 依据手册 p24：切换公司时，账号在该公司被禁用则提示「账号被禁用，请联系管理员」
          setNotice(c.state === '禁用' ? PORTAL_NOTICES[2].text : '');
        },
      })),
    ],
  };

  return (
    <div className="portal-page">
      <div className="portal-header">
        <div className="portal-brand">
          <img src="/logo.png" alt="logo" />
          {platformName}
        </div>
        <div className="portal-header-right">
          <Dropdown menu={companyMenu} placement="bottomRight" trigger={['hover']}>
            <div className="portal-company">
              <BankOutlined />
              {company}
              <SwapOutlined style={{ fontSize: 12 }} />
            </div>
          </Dropdown>
          <Dropdown
            placement="bottomRight"
            trigger={['click']}
            menu={{
              selectable: true,
              selectedKeys: [lng],
              items: LANGS.map((l) => ({
                key: l.key,
                label: l.label,
                onClick: () => { i18n.changeLanguage(l.key); setLng(l.key); },
              })),
            }}
          >
            <GlobalOutlined className="portal-lang" title="切换语言" />
          </Dropdown>
          <Badge count={3} size="small">
            <BellOutlined style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
          </Badge>
          <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
            <div className="portal-user">
              <UserOutlined />
              {user.name}
              <DownOutlined style={{ fontSize: 10 }} />
            </div>
          </Dropdown>
        </div>
      </div>

      <div className="portal-main">
        <div className="portal-welcome">您好，{user.name} 👋</div>
        <div className="portal-welcome-sub">
          当前公司：{company} · 请选择需要进入的产品
        </div>

        {notice ? (
          <div className="portal-notice">
            <span>{notice}</span>
            <CloseOutlined onClick={() => setNotice('')} />
          </div>
        ) : null}

        <div className="portal-section-title">我的产品</div>
        <div className="portal-grid">
          {PRODUCTS.map((p) => {
            const lockedVip = p.key === '3d' && !superAdmin;
            const badge = lockedVip ? '增值功能待开通' : p.badge;
            const locked = (badge === '待开通' && !p.allowEnter) || lockedVip;
            return (
              <div key={p.key} className="portal-card" onClick={() => enterProduct(p)}>
                <span className={`portal-badge ${p.vip ? 'vip' : ''}${badge === '待开通' || lockedVip ? ' off' : ''}`}>{badge}</span>
                <div className="portal-card-icon">{p.icon}</div>
                <div className="portal-card-name">{p.name}</div>
                <div className="portal-card-desc">{p.desc}</div>
                <div className="portal-card-enter">{locked ? '暂无权限 →' : '进入产品 →'}</div>
              </div>
            );
          })}
        </div>

        <div className="portal-section-title" style={{ marginTop: 40 }}>快捷入口</div>
        <div className="portal-grid">
          <div className="portal-card" onClick={() => navigate('/portal/settings')}>
            <div className="portal-card-icon"><SettingOutlined /></div>
            <div className="portal-card-name">设置中心</div>
            <div className="portal-card-desc">组织成员、部门、功能角色与个性配置</div>
            <div className="portal-card-enter">进入设置 →</div>
          </div>
          <div className="portal-card" onClick={() => navigate('/portal/profile')}>
            <div className="portal-card-icon"><UserOutlined /></div>
            <div className="portal-card-name">个人中心</div>
            <div className="portal-card-desc">修改姓名、密码、手机号与邮箱</div>
            <div className="portal-card-enter">进入个人中心 →</div>
          </div>
          <div className="portal-card" onClick={() => navigate('/app/alarm/fault')}>
            <div className="portal-card-icon"><BellOutlined /></div>
            <div className="portal-card-name">报警中心</div>
            <div className="portal-card-desc">故障报警、电池预警与通知管理</div>
            <div className="portal-card-enter">查看报警 →</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalPage;